import React from "react";
import { motion } from "framer-motion";
import { Mail, Phone } from "lucide-react";
import { useTranslation } from "react-i18next";

const Contact: React.FC = () => {
    const { t } = useTranslation();

    const contactMethods = [
        {
            icon: Mail,
            title: "Email Us",
            content: t('contact.email'),
            action: `mailto:${t('contact.email')}`,
            isLink: true
        },
        {
            icon: Phone,
            title: "Call Us",
            content: "+51 999 413 975",
            action: "tel:+51999413975",
            isLink: true
        },
    ];

    return (
        <div className='w-full py-16 md:py-24 bg-gradient-to-br from-[#f84831] via-[#ff6b54] to-[#f84831]'>
            <div className='max-w-6xl mx-auto px-4'>
                {/* Section Header */}
                <motion.div 
                    className="text-center mb-12 md:mb-16"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                >
                    <h2 className='text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 text-white'>
                        {t('contact.title')}
                    </h2>
                    <div className="mx-auto h-1 w-16 md:w-24 bg-white mb-6 rounded-full"></div>
                    <p className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto leading-relaxed">
                        We're here to help with all your produce inspection needs
                    </p>
                </motion.div>

                {/* Contact Methods Grid */}
                <div className="grid md:grid-cols-2 gap-6 md:gap-8 mb-12 max-w-4xl mx-auto">
                    {contactMethods.map((method, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: index * 0.1 }}
                            className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-white/20 hover:bg-white/20 transition-all duration-300 group"
                        >
                            <div className="flex flex-col items-center text-center">
                                <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-white/30 transition-colors duration-300">
                                    <method.icon className="w-8 h-8 text-white" />
                                </div>
                                <h3 className="text-xl font-semibold text-white mb-2">{method.title}</h3>
                                {method.isLink ? (
                                    <a 
                                        href={method.action} 
                                        className="text-white/90 hover:text-white transition-colors duration-300 underline decoration-white/50 hover:decoration-white"
                                    >
                                        {method.content}
                                    </a>
                                ) : (
                                    <p className="text-white/90">{method.content}</p>
                                )}
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Primary Email CTA */}
                <motion.div 
                    className="text-center"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                >
                    <p className="text-white/80 mb-6 text-lg">Ready to ensure your produce quality?</p>
                    <a 
                        href={`mailto:${t('contact.email')}`} 
                        className='inline-flex items-center bg-white text-[#f84831] font-bold rounded-xl px-8 py-4 text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-2xl shadow-lg'
                    >
                        <Mail className="w-5 h-5 mr-2" />
                        {t('contact.email')}
                    </a>
                </motion.div>
            </div>
        </div>
    );
};

export default Contact;